import { getPersistedGrpcQueryEndpoint } from "@/utils/connect-query/query-cache-persistence/persisted-grpc-query-endpoints";
import { DescMessage, MessageShape } from "@bufbuild/protobuf";
import { ConnectError } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { UseQueryResult } from "@tanstack/react-query";
import { createContext, useContext } from "react";

/**
 * True while the persisted gRPC query cache for the current scope is being read from IndexedDB.
 * Only queries for PERSISTED_GRPC_QUERY_ENDPOINTS wait on it (via usePersistedGrpcQuery); every
 * other query fetches immediately. TanStack's app-wide IsRestoringProvider is deliberately not
 * used: it holds every observer in the app until the restore finishes.
 */
const PersistedGrpcQueryRestoringContext = createContext(false);

export const PersistedGrpcQueryRestoringProvider = PersistedGrpcQueryRestoringContext.Provider;

export function useIsRestoringPersistedGrpcQueries(): boolean {
  return useContext(PersistedGrpcQueryRestoringContext);
}

/**
 * `useQuery` that, for a persisted endpoint, stays disabled until the persisted cache restore
 * finishes, so it reads the hydrated response instead of racing it with a network fetch. For any
 * other endpoint it is exactly `useQuery`.
 *
 * The observer stays subscribed while gated, so hydrate() delivers restored data immediately;
 * a cache miss fetches as soon as the gate drops. While gated with no data, the result reports
 * isLoading/isFetching so consumers render their loading state rather than an empty one.
 */
export function usePersistedGrpcQuery<I extends DescMessage, O extends DescMessage>(
  method: Parameters<typeof useQuery<I, O>>[0],
  input: Parameters<typeof useQuery<I, O>>[1],
  options: Parameters<typeof useQuery<I, O>>[2],
): UseQueryResult<MessageShape<O>, ConnectError> {
  const isRestoring = useIsRestoringPersistedGrpcQueries();
  const isGated =
    isRestoring && getPersistedGrpcQueryEndpoint(method.parent.typeName, method.name) != null;
  const callerEnabled = options?.enabled ?? true;

  const result = useQuery(method, input, {
    ...options,
    enabled: callerEnabled && !isGated,
  });

  if (!isGated || !callerEnabled || result.data !== undefined) {
    return result;
  }
  return withRestoringFetchStatus(result);
}

/**
 * Overrides only the fetch-status fields. The other fields forward lazily so TanStack's
 * tracked-result proxy still sees which properties a component actually reads (spreading the
 * result would mark every property as tracked for the observer's lifetime).
 */
function withRestoringFetchStatus<TData, TError>(
  result: UseQueryResult<TData, TError>,
): UseQueryResult<TData, TError> {
  const overrides: Record<string, unknown> = {
    isLoading: true,
    isFetching: true,
    fetchStatus: "fetching",
  };
  const gated = {};
  for (const key of Object.keys(result)) {
    Object.defineProperty(gated, key, {
      enumerable: true,
      get: () =>
        key in overrides ? overrides[key] : (result as unknown as Record<string, unknown>)[key],
    });
  }
  return gated as UseQueryResult<TData, TError>;
}
